import React, { useMemo, useState } from 'react';
import { SearchIcon, ExternalLinkIcon } from 'lucide-react';
import { Project } from '../types';
import { ageBands, difficulties, messLevels, projects, timeBands } from '../data/projects';
import { booksByNumber } from '../data/books';
import { toView } from '../data/links';
type FacetKey = 'age' | 'timeBand' | 'difficulty' | 'mess';
const facets: {
  key: FacetKey;
  label: string;
  options: readonly string[];
}[] = [{
  key: 'age',
  label: 'Age',
  options: ageBands
}, {
  key: 'timeBand',
  label: 'Build time',
  options: timeBands
}, {
  key: 'difficulty',
  label: 'Difficulty',
  options: difficulties
}, {
  key: 'mess',
  label: 'Mess level',
  options: messLevels
}];
const PAGE_SIZE = 30;
const pad = (value: number) => String(value).padStart(2, '0');

/* Searches the ORIGINAL twelve-book library only. The six Build & Discover
   books live at the top of the page and are not indexed here. */
export function ProjectSearch() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Record<FacetKey, string[]>>({
    age: [],
    timeBand: [],
    difficulty: [],
    mess: []
  });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const activeCount = Object.values(selected).reduce((total, values) => total + values.length, 0);
  const isFiltered = activeCount > 0 || query.trim().length > 0;
  const toggle = (key: FacetKey, option: string) => {
    setVisible(PAGE_SIZE);
    setSelected(prev => {
      const values = prev[key];
      return {
        ...prev,
        [key]: values.includes(option) ? values.filter(value => value !== option) : [...values, option]
      };
    });
  };
  const clearAll = () => {
    setQuery('');
    setSelected({
      age: [],
      timeBand: [],
      difficulty: [],
      mess: []
    });
    setVisible(PAGE_SIZE);
  };
  const results = useMemo<Project[]>(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return projects.filter(project => {
      if (terms.length) {
        const book = booksByNumber[project.bookNumber];
        const haystack = `${project.title} ${project.subtitle} ${book ? book.title : ''} ${project.tags.join(' ')}`.toLowerCase();
        if (!terms.every(term => haystack.includes(term))) return false;
      }
      if (selected.age.length && !selected.age.includes(project.age)) return false;
      if (selected.timeBand.length && !selected.timeBand.includes(project.timeBand)) return false;
      if (selected.difficulty.length && !selected.difficulty.includes(project.difficulty)) return false;
      if (selected.mess.length && !selected.mess.includes(project.mess)) return false;
      return true;
    });
  }, [query, selected]);
  const shown = results.slice(0, visible);
  return <div id="search" className="scroll-mt-24" data-id="element-200" data-mp="ux6s6s6qrz7i">
      <label htmlFor="project-query" className="block font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-navy/55" data-id="element-201" data-mp="2l9dv1i31k6dr">
        Search the twelve-book library
      </label>
      <div className="mt-2 flex items-center gap-3 rounded-[12px] border border-navy/15 bg-paper px-4 focus-within:border-navy/45" data-id="element-202" data-mp="dmul874im3nx">
        <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-navy/40" strokeWidth={2} data-id="element-203" data-mp="9n2apovk9nfx" />
        <input id="project-query" type="search" value={query} placeholder="bridge, glider, magnet, straws, gears…" onChange={event => {
        setQuery(event.target.value);
        setVisible(PAGE_SIZE);
      }} className="w-full bg-transparent py-3 font-sans text-[16px] text-navy placeholder:text-navy/35 focus:outline-none" data-id="element-204" data-mp="ndxh8uil7sgc" />
      </div>

      <div className="mt-7 grid gap-6 sm:grid-cols-2" data-id="element-205" data-mp="169xeokuc3o7b">
        {facets.map(facet => <fieldset key={facet.key} data-id="element-206" data-mp="11dq5mj6n5wl5">
            <legend className="font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-navy/45" data-id="element-207" data-mp="3834sfpuatxr3">
              {facet.label}
            </legend>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-208" data-mp="3364mrg1d2x0i">
              {facet.options.map(option => {
            const isOn = selected[facet.key].includes(option);
            return <button key={option} type="button" aria-pressed={isOn} onClick={() => toggle(facet.key, option)} className={`rounded-full border px-3.5 py-1.5 font-sans text-[12.5px] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white ${isOn ? 'border-navy bg-navy text-paper' : 'border-navy/15 text-navy/70 hover:border-navy/45 hover:text-navy'}`} data-id="element-209" data-mp="39iqqfc0kpf6x">
                    {option}
                  </button>;
          })}
            </div>
          </fieldset>)}
      </div>

      <div className="mt-8 flex flex-wrap items-baseline justify-between gap-3 border-t border-navy/10 pt-4" data-id="element-210" data-mp="20k1rg8kjk8y1">
        <p aria-live="polite" className="tnum font-sans text-[12.5px] text-navy/60" data-id="element-211" data-mp="1vl8gamsq8u11">
          {results.length} {results.length === 1 ? 'project' : 'projects'}
          {isFiltered ? ' · filtered' : ''}
        </p>
        {isFiltered ? <button type="button" onClick={clearAll} className="font-sans text-[12.5px] text-navy/60 underline decoration-navy/25 underline-offset-4 transition-colors duration-150 ease-out hover:text-navy hover:decoration-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy" data-id="element-212" data-mp="1llzq850zfzjy">
            Clear everything
          </button> : null}
      </div>

      {results.length === 0 ? <p className="mt-8 max-w-[34rem] font-sans text-[15.5px] leading-relaxed text-navy/60" data-id="element-213" data-mp="xwylosv9t8jy">
          Nothing matches that combination. Try one filter fewer, or search a material instead of a
          machine: <span className="text-navy" data-id="element-214" data-mp="j9zyja3g50b1">straws</span>, <span className="text-navy" data-id="element-215" data-mp="j8ybe795oncy">foil</span>,{' '}
          <span className="text-navy" data-id="element-216" data-mp="oa44452tgamh">string</span>.
        </p> : <>
          <ul className="mt-6 divide-y divide-navy/10 border-t border-navy/10" data-id="element-217" data-mp="aa32gneq8z5y">
            {shown.map(project => {
          const book = booksByNumber[project.bookNumber];
          const href = book ? toView(book.file) : '';
          return <li key={project.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3" data-id="element-218" data-mp="12zaacd02n13v">
                  <span aria-hidden="true" className="mt-[7px] h-2 w-2 shrink-0 self-start rounded-full" style={{
              backgroundColor: book ? book.accent : '#7B8EA3'
            }} data-id="element-219" data-mp="16zatgay480h9" />
                  <div className="min-w-[12rem] flex-1" data-id="element-220" data-mp="1lwtb6tcdldf7">
                    <p className="font-display text-[17px] leading-snug text-navy" data-id="element-221" data-mp="1lv9wv8aa5cg2">{project.title}</p>
                    <p className="font-sans text-[14px] leading-snug text-navy/50" data-id="element-222" data-mp="1qq60l5ovpo90">
                      {project.subtitle}
                    </p>
                  </div>
                  <p className="tnum font-sans text-[12px] text-navy/55" data-id="element-223" data-mp="1w0ryb02eiyfu">
                    {project.age} · {project.time} ·{' '}
                    {project.mess === 'None' ? 'no mess' : `${project.mess.toLowerCase()} mess`}
                  </p>
                  <p className="tnum font-sans text-[12px] text-navy/45" data-id="element-224" data-mp="1h3r3uostspdo">
                    Book {pad(project.bookNumber)} · Project {pad(project.projectNumber)} · p.
                    {project.page}
                  </p>
                  {href ? <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-sans text-[12px] text-navy/70 underline decoration-navy/25 underline-offset-4 transition-colors duration-150 ease-out hover:text-navy hover:decoration-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy" data-id="element-225" data-mp="1r3roce2flbjg">
                      <ExternalLinkIcon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2} data-id="element-226" data-mp="zu6rbb8gmlis" />
                      Open book
                    </a> : null}
                </li>;
        })}
          </ul>
          {results.length > visible ? <button type="button" onClick={() => setVisible(prev => prev + PAGE_SIZE)} className="mt-6 rounded-[12px] border border-navy/20 px-4 py-2.5 font-sans text-[13px] font-medium text-navy transition-colors duration-150 ease-out hover:border-navy/45 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-227" data-mp="1ejurg94wne4x">
              Show {Math.min(PAGE_SIZE, results.length - visible)} more
            </button> : null}
        </>}
    </div>;
}